import {defineStore} from 'pinia';
import {computed, ref, watch} from 'vue';
import {orderBy} from 'lodash-es';
import MortgageRealEstatesApi from './MortgageRealEstatesApi';
import type {
  MortgageRealEstateMarketType,
  MortgageRealEstateOffer,
  MortgageRealEstatePropertyType,
  MortgageRealEstateSortProperty,
} from '@/modules/calculator-v25/types/mortgage/realEstates';
import {useSimulationStore} from '@/components/calculator/services/SimulationStore';
import {useInstance} from '@/composables/useInstance';
import useGeoAreas from '@/composables/useGeoAreas';
import {LoanPurposeWhat} from '@/models/simulation/LoanPurposeWhat';
import {MarketType} from '@/models/simulation/MarketType';
import {debouncedAtInput} from '@/services/utils/BasicUtils';
import {textNormalizer} from '@/services/utils/StringUtils';
import EnvironmentService from '@/env/EnvironmentService';
import {I18NGetter} from '@/services/enumTranslator/I18NGetter';
import type {filterStatateStoreStateForApi} from '@/modules/realEstates/filters';

const STORE_ID = 'mortgage-real-estates-store';
const SELECTION_STORAGE_KEY = `${STORE_ID}-selection`;

const DEFAULT_SORT_BY: MortgageRealEstateSortProperty = 'createdAt:desc';

export const MORTGAGE_REAL_ESTATES_PAGE_SIZE = 24;

type MortgageRealEstatesViewState = 'not-searchable' | 'loading' | 'error' | 'empty' | 'offers';

type MortgageRealEstatesCriteria = {
  realEstateType: Nullable<LoanPurposeWhat>;
  marketType: Nullable<MarketType>;
  province: Nullable<string>;
  district: Nullable<string>;
  maxPrice: Nullable<number>;
};

// The property listing indexes flats and houses only — every other collateral (plot, office…)
// has no counterpart there, so the tab has nothing to match against.
const PROPERTY_TYPE_BY_REAL_ESTATE_TYPE: Partial<Record<LoanPurposeWhat, MortgageRealEstatePropertyType>> = {
  [LoanPurposeWhat.APARTMENT]: 'Flat',
  [LoanPurposeWhat.HOUSE]: 'House',
};

const PROPERTY_MARKET_TYPE_BY_MARKET_TYPE: Record<MarketType, MortgageRealEstateMarketType> = {
  [MarketType.PRIMARY_MARKET]: 'Primary',
  [MarketType.SECONDARY_MARKET]: 'Secondary',
};

// Lendi's dictionary spells cities with county rights as "m. Kraków" while the property index
// knows them as plain `malopolskie/krakow` — leave the "m." in and nothing ever matches.
const toLocationSlug = (name: string): string => textNormalizer(name.replace(/^m\.\s*/, ''))
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

// `locationHierarchy` carries the whole chain (województwo → powiat → gmina → miasto → dzielnica),
// so a single filter serves both levels and the powiat slug already implies its województwo.
const buildLocationSlug = (criteria: MortgageRealEstatesCriteria): Nullable<string> => {
  if (!criteria.province) {
    return null;
  }
  const province = toLocationSlug(criteria.province);
  return criteria.district ? `${province}/${toLocationSlug(criteria.district)}` : province;
};

const buildFilterBy = (criteria: MortgageRealEstatesCriteria): string => {
  const propertyType = criteria.realEstateType ? PROPERTY_TYPE_BY_REAL_ESTATE_TYPE[criteria.realEstateType] : null;
  const locationSlug = buildLocationSlug(criteria);
  return [
    propertyType && `propertyType:=[${propertyType}]`,
    criteria.marketType && `marketType:=[${PROPERTY_MARKET_TYPE_BY_MARKET_TYPE[criteria.marketType]}]`,
    locationSlug && `locationHierarchy:=[\`${locationSlug}\`]`,
    criteria.maxPrice && `priceAmount:<=${criteria.maxPrice}`,
  ].filter(Boolean).join(' && ');
};

// The same search in the property listing's own filters — what a deal shows as the properties' search parameters.
// Only the criteria the search used: the filters ride in the new-deal modal URL, which has a length limit.
const buildSearchFilters = (criteria: MortgageRealEstatesCriteria): Nullable<Partial<filterStatateStoreStateForApi>> => {
  const propertyType = criteria.realEstateType ? PROPERTY_TYPE_BY_REAL_ESTATE_TYPE[criteria.realEstateType] : null;
  if (!propertyType) {
    return null;
  }
  const locationSlug = buildLocationSlug(criteria);
  return {
    propertyType,
    ...(criteria.marketType ? {marketType: PROPERTY_MARKET_TYPE_BY_MARKET_TYPE[criteria.marketType],} : {}),
    ...(locationSlug ? {mainLocation: locationSlug,} : {}),
    ...(criteria.maxPrice ? {priceAmount: {min: null, max: criteria.maxPrice,},} : {}),
  };
};

export const useMortgageRealEstatesStore = defineStore(STORE_ID, () => {
  const {$errorHandler, $user,} = useInstance();

  const simulationStore = useSimulationStore();
  const {getLandNameById, getDistrictNameById,} = useGeoAreas();

  const offers = ref<MortgageRealEstateOffer[]>([]);
  const totalFound = ref<Nullable<number>>(null);
  const page = ref<number>(1);
  const sortBy = ref<MortgageRealEstateSortProperty>(DEFAULT_SORT_BY);
  const loading = ref<boolean>(false);
  const hasError = ref<boolean>(false);
  const selectedOffers = ref<MortgageRealEstateOffer[]>([]);
  const showOnlySelected = ref<boolean>(false);
  // Starts from the feature flag — an admin can switch it in the comparator settings until the next reload.
  const isEnabled = ref<boolean>(EnvironmentService.Environment.isComparatorRealEstatesOn);

  // Only the newest search may write to the state — dragging a slider fires searches faster than they resolve.
  let lastRequestId = 0;

  const isMarketTypeApplicable = computed<boolean>(() =>
    EnvironmentService.Environment.getAppDomainConfig().calculator.hasMarketTypes &&
    (simulationStore.isLoanPurposeActivityBuying || simulationStore.isLoanPurposeActivityBuyingAndRenovating));

  const criteria = computed<MortgageRealEstatesCriteria>(() => {
    const userInput = simulationStore.userInput;
    const household = userInput?.households?.[0];
    const landId = household?.landId ?? null;
    const districtId = household?.districtId ?? null;
    return {
      realEstateType: userInput?.loanPurpose.what ?? userInput?.collateral ?? null,
      marketType: isMarketTypeApplicable.value ? userInput?.loanPurpose.market ?? null : null,
      province: landId ? getLandNameById(landId) ?? null : null,
      district: districtId ? getDistrictNameById(districtId) ?? null : null,
      maxPrice: userInput?.hypothecValue ?? null,
    };
  });

  const criteriaKey = computed<string>(() => JSON.stringify(criteria.value));

  const searchFilters = computed<Nullable<Partial<filterStatateStoreStateForApi>>>(() => buildSearchFilters(criteria.value));

  // Until enabled the tab ships the coming-soon placeholder, so nothing here may reach the property index.
  const isSearchable = computed<boolean>(() =>
    isEnabled.value &&
    $user.value.canAccessModule.REAL_ESTATES && simulationStore.isMortgage && !!criteria.value.realEstateType &&
    !!PROPERTY_TYPE_BY_REAL_ESTATE_TYPE[criteria.value.realEstateType]);

  const pagesCount = computed<number>(() => Math.ceil((totalFound.value ?? 0) / MORTGAGE_REAL_ESTATES_PAGE_SIZE));

  const selectedOfferIds = computed<string[]>(() => selectedOffers.value.map(x => x.id));

  // Selected offers are kept whole, so they can be shown together no matter which pages they were picked on.
  // Sorted like the search: offers missing the field go last either way, ties follow the index order (UUIDv7 ids ≈ `_seq_id`).
  const visibleOffers = computed<MortgageRealEstateOffer[]>(() => {
    if (!showOnlySelected.value) {
      return offers.value;
    }
    const [field, direction,] = sortBy.value.split(':') as [keyof MortgageRealEstateOffer, 'asc' | 'desc'];
    return orderBy(selectedOffers.value, [x => x[field] == null, field, 'id',], ['asc', direction, direction,]);
  });

  const viewState = computed<MortgageRealEstatesViewState>(() => {
    if (!isSearchable.value) {
      return 'not-searchable';
    }
    // Re-sorting still searches in the background — it must not swap the selected offers for a skeleton.
    if (showOnlySelected.value) {
      return 'offers';
    }
    if (loading.value) {
      return 'loading';
    }
    if (hasError.value) {
      return 'error';
    }
    return offers.value.length ? 'offers' : 'empty';
  });

  const search = async (): Promise<void> => {
    if (!isSearchable.value) {
      offers.value = [];
      totalFound.value = null;
      return;
    }
    const requestId = ++lastRequestId;
    loading.value = true;
    hasError.value = false;
    try {
      const result = await MortgageRealEstatesApi.searchOffers({
        filterBy: buildFilterBy(criteria.value),
        sortBy: sortBy.value,
        page: page.value,
        perPage: MORTGAGE_REAL_ESTATES_PAGE_SIZE,
      });
      if (requestId !== lastRequestId) {
        return;
      }
      offers.value = result.offers;
      totalFound.value = result.found;
    } catch (error: any) {
      if (requestId !== lastRequestId) {
        return;
      }
      offers.value = [];
      totalFound.value = null;
      hasError.value = true;
      // The search also runs while the advisor sits on the offers tab (it feeds the tab counter) —
      // only snackbar a failure they can actually see, the tab itself renders its own error state.
      if (simulationStore.resultsTab === 'real-estates') {
        $errorHandler(error, I18NGetter().useMortgageRealEstatesStore.SEARCH_ERROR);
      }
    } finally {
      if (requestId === lastRequestId) {
        loading.value = false;
      }
    }
  };

  const changePage = (nextPage: number): Promise<void> => {
    page.value = nextPage;
    return search();
  };

  const searchFromFirstPage = (): Promise<void> => {
    page.value = 1;
    return search();
  };

  const changeSort = (nextSortBy: MortgageRealEstateSortProperty): Promise<void> => {
    sortBy.value = nextSortBy;
    return searchFromFirstPage();
  };

  const toggleOfferSelection = (offer: MortgageRealEstateOffer): void => {
    selectedOffers.value = selectedOfferIds.value.includes(offer.id)
      ? selectedOffers.value.filter(x => x.id !== offer.id)
      : [...selectedOffers.value, offer,];
  };

  const unselectOffers = (offerIds: string[]): void => {
    selectedOffers.value = selectedOffers.value.filter(x => !offerIds.includes(x.id));
  };

  // A reload brings the selection back only for the simulation and criteria it was made for.
  const readStoredSelection = (): MortgageRealEstateOffer[] => {
    try {
      const stored: Nullable<{simulationId: string; criteriaKey: string; offers: MortgageRealEstateOffer[];}> =
        JSON.parse(sessionStorage.getItem(SELECTION_STORAGE_KEY) ?? 'null');
      return stored?.simulationId === simulationStore.simulationId && stored?.criteriaKey === criteriaKey.value
        ? stored.offers
        : [];
    } catch {
      return [];
    }
  };

  const debouncedSearchFromFirstPage = debouncedAtInput(searchFromFirstPage);

  // Flip to loading the moment the criteria move, not when the debounced search finally fires —
  // otherwise the tab flashes its empty state for the length of the debounce.
  const scheduleSearch = (): void => {
    loading.value = isSearchable.value;
    debouncedSearchFromFirstPage();
  };

  const clear = (): void => {
    selectedOffers.value = [];
    sortBy.value = DEFAULT_SORT_BY;
    try {
      // Right after a reload the selection is still empty while its stored copy waits for the criteria to load.
      sessionStorage.removeItem(SELECTION_STORAGE_KEY);
    } catch {
      /* storage blocked — nothing was stored */
    }
    // Debounced, so when the cleared form also changes the criteria, their watcher's search merges with this one.
    scheduleSearch();
  };

  watch([criteriaKey, isEnabled, () => simulationStore.simulationId,], () => {
    selectedOffers.value = isSearchable.value ? readStoredSelection() : [];
    scheduleSearch();
  }, {immediate: true,});

  // Watches the ids, not the array: re-emptying an empty selection while a reload loads the criteria must not wipe the stored one.
  watch(() => selectedOfferIds.value.join(), () => {
    try {
      // Removed first, so a write the storage refuses (quota) cannot leave an older selection behind.
      sessionStorage.removeItem(SELECTION_STORAGE_KEY);
      if (simulationStore.simulationId && selectedOffers.value.length) {
        sessionStorage.setItem(SELECTION_STORAGE_KEY, JSON.stringify({
          simulationId: simulationStore.simulationId,
          criteriaKey: criteriaKey.value,
          offers: selectedOffers.value,
        }));
      }
    } catch {
      /* storage blocked or full — the selection just won't survive a reload */
    }
  }, {flush: 'sync',});

  watch(() => selectedOffers.value.length, length => {
    if (!length) {
      showOnlySelected.value = false;
    }
  }, {flush: 'sync',});

  return {
    offers,
    totalFound,
    page,
    sortBy,
    selectedOfferIds,
    showOnlySelected,
    isEnabled,
    visibleOffers,
    pagesCount,
    criteria,
    searchFilters,
    viewState,
    search,
    changePage,
    changeSort,
    toggleOfferSelection,
    unselectOffers,
    clear,
  };
});
